import { PickOrCreate } from '../primitives/PickOrCreate';
import {
  formatPropertyPillLabel,
  isPropertyPillMuted
} from '../primitives/propertyBarDisplay';
import { useDocumentTypeOptions } from '../../hooks/useDocumentTypeOptions';

export interface DocumentTypePickerProps {
  orgId: string;
  /** The document's current type_id (required — every document has a Type). */
  typeId: string;
  editable: boolean;
  /** Persist a type change; returns false on failure (error surfaced via onError). */
  onSetType: (typeId: string) => Promise<boolean>;
  onError?: (message: string) => void;
  testId?: string;
}

/**
 * The single "Type" control for a document — a pill-variant PickOrCreate over
 * the org's document_types (reusing the property-bar pill look). Picking assigns
 * via set_document_type; "+ Create" mints an org-saved type via
 * create_document_type and assigns it. Type is required, so there is no clear
 * affordance (the pill variant has none).
 */
export function DocumentTypePicker({
  orgId,
  typeId,
  editable,
  onSetType,
  onError,
  testId = 'document-editor-v2-type-picker'
}: DocumentTypePickerProps) {
  const { options, createType } = useDocumentTypeOptions(orgId, typeId, onError);
  const current = options.find((o) => o.id === typeId) ?? null;
  const name = current?.name ?? null;

  return (
    <PickOrCreate
      id="document-editor-type-picker"
      label="Type"
      options={options.map((o) => ({ id: o.id, label: o.name }))}
      value={{ option_id: typeId }}
      disabled={!editable}
      triggerVariant="pill"
      pillLabel={formatPropertyPillLabel('Type', name)}
      pillMuted={isPropertyPillMuted(name)}
      data-testid={testId}
      onPick={(picked) => {
        if (picked && picked.id !== typeId) void onSetType(picked.id);
      }}
      onCreate={async (label) => {
        const t = await createType(label);
        // The picker commits the returned option via onPick, which assigns it.
        return t ? { id: t.id, label: t.name } : null;
      }}
    />
  );
}
